import { useState, useEffect } from "react";
import {
  Dialog,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import theme from "../../theme/theme";
import { fetchBanks } from "../../api/BankService";

// Auto-generate the AMC lift description from the project's lift type + capacity.
const buildLiftDescription = (project) => {
  if (!project) return "";
  const type = project.lift_type
    ? project.lift_type.charAt(0).toUpperCase() +
      project.lift_type.slice(1).replace(/_/g, " ")
    : "Passenger";
  const capacity = project.capacity_persons
    ? ` - ${project.capacity_persons} Persons Capacity`
    : "";
  return `Electric ${type} Lift${capacity}`;
};

// When `service` is set, the dialog works in edit mode.
const CreateServiceDialog = ({ open, onClose, saving, onSave, project, service }) => {
  const isEdit = Boolean(service);
  const [form, setForm] = useState({
    start_date: "",
    interval_in_days: 30,
    number_of_services: 12,
    service_type: "free_service",
    price: 0,
    reference_no: "",
    lift_description: "",
    bank_id: "",
  });
  const [errors, setErrors] = useState({});
  const [banks, setBanks] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (service) {
      setForm({
        start_date: service.start_date
          ? String(service.start_date).slice(0, 10)
          : "",
        interval_in_days: service.interval_in_days ?? 30,
        number_of_services: service.number_of_services ?? 12,
        service_type: service.service_type || "free_service",
        price: service.price ?? 0,
        reference_no: service.reference_no || "",
        lift_description: service.lift_description || "",
        bank_id: service.bank_id || "",
      });
    } else {
      setForm((f) => ({
        ...f,
        lift_description: buildLiftDescription(project),
      }));
    }
    setErrors({});
    fetchBanks()
      .then((res) => {
        const data = res.data?.data || res.data || [];
        setBanks(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        // silent — bank selection stays empty
      });
  }, [open, project, service]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validate = () => {
    const errs = {};
    if (!form.start_date) errs.start_date = "Start date is required";
    if (!form.interval_in_days || form.interval_in_days < 1)
      errs.interval_in_days = "Interval must be at least 1 day";
    if (!form.number_of_services || form.number_of_services < 1)
      errs.number_of_services = "At least 1 service required";
    if (!form.service_type) errs.service_type = "Service type is required";
    if (form.service_type === "paid_service" && (!form.price || form.price < 0))
      errs.price = "Price is required for paid services";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSave({
      ...form,
      interval_in_days: parseInt(form.interval_in_days),
      number_of_services: parseInt(form.number_of_services),
      price: parseFloat(form.price) || 0,
      reference_no: form.reference_no.trim() || null,
      lift_description: form.lift_description.trim() || null,
      bank_id: form.bank_id || null,
    });
  };

  const handleClose = () => {
    setForm({
      start_date: "",
      interval_in_days: 30,
      number_of_services: 12,
      service_type: "free_service",
      price: 0,
      reference_no: "",
      lift_description: "",
      bank_id: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: theme.dialog.borderRadius } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 2,
          backgroundColor: theme.dialog.headerBgColor,
        }}
      >
        <Typography variant="h6" fontWeight="700">
          {isEdit ? "Edit Service Plan" : "Create Service Plan"}
        </Typography>
        <IconButton size="small" onClick={handleClose}>
          <Close />
        </IconButton>
      </Box>

      {/* Body */}
      <Box
        sx={{
          px: 3,
          py: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        <TextField
          name="service_type"
          label="Service Type"
          select
          fullWidth
          size="small"
          value={form.service_type}
          onChange={handleChange}
          error={!!errors.service_type}
          helperText={errors.service_type}
        >
          <MenuItem value="free_service">Free Service</MenuItem>
          <MenuItem value="paid_service">Paid Service</MenuItem>
        </TextField>

        {form.service_type === "paid_service" && (
          <TextField
            name="price"
            label="Price per Service (₹)"
            type="number"
            fullWidth
            size="small"
            value={form.price}
            onChange={handleChange}
            error={!!errors.price}
            helperText={errors.price}
          />
        )}

        <TextField
          name="start_date"
          label="Start Date"
          type="date"
          fullWidth
          size="small"
          value={form.start_date}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          error={!!errors.start_date}
          helperText={
            errors.start_date ||
            (isEdit
              ? "Changing the schedule regenerates all service visits"
              : "")
          }
        />

        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            name="interval_in_days"
            label="Interval (Days)"
            type="number"
            fullWidth
            size="small"
            value={form.interval_in_days}
            onChange={handleChange}
            error={!!errors.interval_in_days}
            helperText={errors.interval_in_days}
          />
          <TextField
            name="number_of_services"
            label="Number of Services"
            type="number"
            fullWidth
            size="small"
            value={form.number_of_services}
            onChange={handleChange}
            error={!!errors.number_of_services}
            helperText={errors.number_of_services}
          />
        </Box>

        <TextField
          name="reference_no"
          label="AMC Reference No"
          fullWidth
          size="small"
          placeholder="e.g. RP/AMC/26-27/01"
          value={form.reference_no}
          onChange={handleChange}
          helperText="Auto-generated if left blank"
        />

        <TextField
          name="lift_description"
          label="Lift Description (for AMC agreement)"
          fullWidth
          size="small"
          value={form.lift_description}
          onChange={handleChange}
          helperText="Auto-generated from project — edit as needed"
        />

        <TextField
          name="bank_id"
          label="Bank (account details on AMC)"
          select
          fullWidth
          size="small"
          value={form.bank_id}
          onChange={handleChange}
        >
          <MenuItem value="">None</MenuItem>
          {banks.map((bank) => (
            <MenuItem key={bank.id} value={bank.id}>
              {bank.name}
              {bank.branch ? ` — ${bank.branch}` : ""}
            </MenuItem>
          ))}
        </TextField>

        {form.start_date &&
          form.interval_in_days > 0 &&
          form.number_of_services > 0 && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                backgroundColor: theme.palette.primary.main + "0D",
                border: "1px solid " + theme.palette.primary.main + "30",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                <strong>Calculated End Date:</strong>{" "}
                {new Date(
                  new Date(form.start_date).getTime() +
                    (parseInt(form.interval_in_days) || 0) *
                      ((parseInt(form.number_of_services) || 1) - 1) *
                      86400000,
                ).toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                <strong>Total Entries:</strong> {form.number_of_services}{" "}
                service visits, every {form.interval_in_days} days
              </Typography>
            </Box>
          )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
          px: 3,
          py: 2,
          backgroundColor: theme.dialog.footerBgColor,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={saving}
          sx={{
            borderRadius: theme.button.borderRadius,
            textTransform: "none",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          sx={{
            borderRadius: theme.button.borderRadius,
            textTransform: "none",
            backgroundColor: theme.palette.primary.main,
          }}
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {saving
            ? isEdit
              ? "Updating..."
              : "Creating..."
            : isEdit
              ? "Update Service"
              : "Create Service"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default CreateServiceDialog;
